import { Card, Group, SimpleGrid, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCircleCheck, TablerIconsProps } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

/**
 * ActionBand — "Needs your attention", the top of the personal Dashboard.
 *
 * The single most important thing on this page: what is waiting on *you*, right now.
 *
 * Items with a zero count are dropped rather than rendered greyed-out, so a user with a
 * clear queue sees one short "all clear" line instead of a wall of zeros. Each card is a
 * link straight to the thing that needs doing — the point is to leave this page, not to
 * admire it.
 *
 * Items are merged across roles: a user who is both a leader and a facilitator gets one
 * band containing both kinds of work, ordered by urgency rather than grouped by role.
 */
export interface ActionItem {
  key: string;
  label: string;
  count: number;
  icon: React.ComponentType<TablerIconsProps>;
  /** Mantine colour token name — use warning/danger tones for genuine backlogs. */
  color: string;
  to?: string;
  onClick?: () => void;
  /** Second line, e.g. "oldest: 32 days". */
  hint?: string;
}

export interface ActionBandProps {
  items: ActionItem[];
  loading?: boolean;
}

export default function ActionBand({ items, loading = false }: ActionBandProps) {
  const navigate = useNavigate();
  const active = items.filter((item) => item.count > 0);

  if (loading && active.length === 0) return null;

  if (active.length === 0) {
    return (
      <Card withBorder radius="md" padding="md">
        <Group gap="sm">
          <ThemeIcon size={34} radius="md" variant="light" color="green">
            <IconCircleCheck size={18} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="sm">You&apos;re all caught up</Text>
            <Text size="xs" c="dimmed">Nothing is waiting on you right now.</Text>
          </div>
        </Group>
      </Card>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: Math.min(active.length, 4) }} spacing="md">
      {active.map((item) => {
        const Icon = item.icon;

        return (
          <UnstyledButton
            key={item.key}
            onClick={() => {
              if (item.onClick) return item.onClick();
              if (item.to) navigate(item.to);
            }}
            style={{ textAlign: 'left' }}
          >
            <Card
              withBorder
              radius="md"
              padding="md"
              style={{
                borderColor: `var(--mantine-color-${item.color}-filled)`,
                backgroundColor: `var(--mantine-color-${item.color}-light)`,
              }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon size={38} radius="md" variant="light" color={item.color}>
                  <Icon size={20} />
                </ThemeIcon>
                <div style={{ minWidth: 0 }}>
                  <Group gap={6} align="baseline" wrap="nowrap">
                    <Text size="xl" fw={700}>{item.count}</Text>
                    <Text size="sm" fw={500} truncate>{item.label}</Text>
                  </Group>
                  {item.hint && (
                    <Text size="xs" c="dimmed" truncate>{item.hint}</Text>
                  )}
                </div>
              </Group>
            </Card>
          </UnstyledButton>
        );
      })}
    </SimpleGrid>
  );
}
